import clsx from 'clsx';
import { StageCandidateActionType } from 'enums/stageAction';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Button } from '@spotted-zebra-uk/ui-components';
import {
  adjustmentsResponsesType,
  hasAuditoryAdjustment,
  hasOnlyUntimedAssessments,
  requiresAdjustments,
  shouldRedirectToIntro,
  steps,
  TAdjustmentsFlowRoutes,
} from '../AccessibilityAdjustments.helpers';
import SupportEmailDisclaimer from '../SupportEmailDisclaimer';
import style from './AccessibilitySummary.module.scss';

const AccessibilitySummary = ({
  flowRoutes,
  responses,
  completedStep,
  tests,
  onSubmit,
  loading,
  isLiveChatEnabled = true,
}: {
  flowRoutes: TAdjustmentsFlowRoutes;
  completedStep: steps;
  responses: adjustmentsResponsesType;
  tests: StageCandidateActionType[];
  onSubmit: (redirectUrl: string, step?: steps) => void;
  loading: boolean;
  /**
   * The company's "Display live chat to users" setting. When off, the copy
   * points candidates to email only.
   */
  isLiveChatEnabled?: boolean;
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  useEffect(() => {
    // Redirect to flow intro page on page refresh
    shouldRedirectToIntro(completedStep) &&
      navigate(flowRoutes.getStepUrl('intro'));
  }, [completedStep, flowRoutes, navigate]);

  const handleGoBack = () => {
    navigate(flowRoutes.getStepUrl('options'));
  };

  const hasAdjustments = requiresAdjustments(responses, tests);

  const handleSubmit = () => {
    const link = hasAdjustments
      ? flowRoutes.getStepUrl('details')
      : flowRoutes.exitUrl;
    onSubmit(link, 'summary');
  };

  return (
    <section className={style.container} data-testid="accessibility-summary">
      {hasAdjustments ? (
        <RequiresAdjustments />
      ) : (
        <>
          <p className={style.description}>
            {t('accessibilityAdjustments.summary.noAdjustmentsNecessary')}
          </p>
          {hasOnlyUntimedAssessments(tests) && (
            <p className={style.boldDescription}>
              {t('accessibilityAdjustments.summary.hasUntimedTests')}
            </p>
          )}
          {hasAuditoryAdjustment(responses) && (
            <p className={style.boldDescription}>
              {t('accessibilityAdjustments.summary.hasAuditoryAdjustment')}
            </p>
          )}
          <SupportEmailDisclaimer
            i18nPrefix="accessibilityAdjustments.summary"
            isLiveChatEnabled={isLiveChatEnabled}
            testIdPrefix="accessibility-summary"
            className={style.disclaimer}
          />
        </>
      )}
      <div
        className={clsx(style.actions, hasAdjustments && style.largeButtons)}
      >
        {hasAdjustments && (
          <Button
            variant="secondary"
            onClick={handleGoBack}
            aria-label={t('accessibilityAdjustments.goBack')}
            data-testid="accessibility-summary__go-back-button"
          >
            {t('accessibilityAdjustments.goBack')}
          </Button>
        )}
        <Button
          onClick={handleSubmit}
          type="submit"
          aria-label={t('common.continue')}
          loading={loading}
          data-testid="accessibility-summary__submit-button"
        >
          {t('common.continue')}
        </Button>
      </div>
    </section>
  );
};

export default AccessibilitySummary;

const RequiresAdjustments = () => {
  const { t } = useTranslation();
  return (
    <>
      <p className={style.description}>
        {t('accessibilityAdjustments.summary.adjustmentsNecessary')}
      </p>
      <p className={style.description}>
        {t('accessibilityAdjustments.summary.confirm')}
      </p>
    </>
  );
};
